import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { api, mediaAssetUrl, mediaDownloadUrl, type GenerationJob, type GenerationJobStatus } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';
import { CostLine } from './StudioCost.js';

/**
 * A generation still moving. Polled until it settles, the same shape as the
 * jobs list's own refetch rule — see JobsPanel.
 */
export function isUnsettled(status: GenerationJobStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING';
}

export function statusClass(status: GenerationJobStatus): string {
  if (status === 'APPROVED' || status === 'DONE') return 'ok';
  if (status === 'FAILED') return 'err';
  if (status === 'RUNNING') return 'accent';
  return 'warn';
}

/**
 * One result: its state, and what can be done with it.
 *
 * Used both while composing and inside a project's page, so what it does
 * with the outcome is left to the caller: `onRevised` gets the new job a
 * revision created, `pick` is a slot for "include this in the post", and
 * `publishMenu` for the hand-off to Publish.
 */
export function ResultCard({
  title,
  job,
  projectId,
  onRevised,
  pick,
  publishMenu,
}: {
  title: string;
  job: GenerationJob | undefined;
  /** The project the revision should land in. */
  projectId?: string | null;
  onRevised: (job: GenerationJob) => void;
  pick?: ReactNode;
  publishMenu?: ReactNode;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);
  const share = useMutation({
    mutationFn: (id: string) => api.shareGenerationJob(id),
    onSuccess: async (link) => {
      setSharedUrl(link.url);
      // Clipboard access can be refused (http, permissions); the link is
      // shown on screen either way, so a refusal costs nothing.
      try {
        await navigator.clipboard.writeText(link.url);
      } catch {
        /* shown below instead */
      }
    },
  });
  const [feedback, setFeedback] = useState('');

  const approve = useMutation({
    mutationFn: (id: string) => api.approveGenerationJob(id),
    onSuccess: (approved) => {
      // The badge and every view of the project move with it.
      queryClient.setQueryData(['generation-job', approved.id], approved);
      void queryClient.invalidateQueries({ queryKey: ['generation-project'] });
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
    },
  });

  const revise = useMutation({
    mutationFn: (id: string) =>
      api.reviseGenerationJob(id, { feedbackText: feedback, ...(projectId ? { projectId } : {}) }),
    onSuccess: (newJob) => {
      onRevised(newJob);
      setFeedback('');
      setFeedbackOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['generation-project'] });
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
    },
  });

  if (!job) return <div className="loading">{t('common.loading')}</div>;

  return (
    <article className="card">
      <header className="card-head">
        <strong>{title}</strong>
        <span className={`badge ${statusClass(job.status)}`}>{job.status}</span>
      </header>

      {isUnsettled(job.status) && <p className="muted small">{t('studio.jobRunning')}</p>}
      {job.status === 'FAILED' && (
        <p className="error small">{job.errorMessage ?? t('studio.jobFailed')}</p>
      )}

      {job.resultText && <p className="text-cell">{job.resultText}</p>}

      {job.status !== 'PENDING' && job.status !== 'RUNNING' && job.resultMediaId && (
        <GenerationResultMedia job={job} />
      )}

      {job.feedbackText && (
        <p className="muted small">{t('studio.previousFeedback', { text: job.feedbackText })}</p>
      )}

      {(job.status === 'DONE' || job.status === 'APPROVED') && <CostLine cost={job} />}

      {pick}

      {(job.status === 'DONE' || job.status === 'APPROVED') && (
        <div className="actions">
          {job.status === 'DONE' && (
            <button
              type="button"
              className="primary"
              onClick={() => approve.mutate(job.id)}
              disabled={approve.isPending}
            >
              <Icon.check />
              {t('studio.approve')}
            </button>
          )}
          <button type="button" className="ghost small" onClick={() => setFeedbackOpen((open) => !open)}>
            {t('studio.reviseWithFeedback')}
          </button>
          {job.resultMediaId && (
            <a className="download-link" href={mediaDownloadUrl(job.resultMediaId)} download>
              <Icon.download />
              {t('studio.download')}
            </a>
          )}
          {job.resultMediaId && (
            <button
              type="button"
              className="ghost small"
              onClick={() => share.mutate(job.id)}
              disabled={share.isPending}
            >
              <Icon.link />
              {t('studio.copyLink')}
            </button>
          )}
        </div>
      )}

      {(job.status === 'DONE' || job.status === 'APPROVED') && publishMenu}

      {sharedUrl && (
        <p className="small">
          {t('studio.linkReady')}{' '}
          <a className="mono ltr" dir="ltr" href={sharedUrl} target="_blank" rel="noreferrer">
            {sharedUrl}
          </a>
        </p>
      )}
      {share.isError && <ErrorNotice error={share.error} />}

      {approve.isError && <ErrorNotice error={approve.error} />}

      {feedbackOpen && (
        <div className="inline-form" style={{ marginTop: 10 }}>
          <textarea
            rows={3}
            value={feedback}
            placeholder={t('studio.feedbackPlaceholder')}
            onChange={(event) => setFeedback(event.target.value)}
          />
          <button
            type="button"
            onClick={() => revise.mutate(job.id)}
            disabled={!feedback.trim() || revise.isPending}
          >
            {t('studio.submitRevision')}
          </button>
        </div>
      )}
      {revise.isError && <ErrorNotice error={revise.error} />}
    </article>
  );
}

/**
 * The result file itself: an audio player, a video player, or an image.
 *
 * TODO: `mediaAssetUrl` is a placeholder — see lib/api.ts. Swap it for the
 * confirmed download route once apps/api/src/routes/generation.ts lands.
 */
function GenerationResultMedia({ job }: { job: GenerationJob }) {
  const src = mediaAssetUrl(job, 'resultMediaId');
  if (!src) return null;

  if (job.capability === 'AUDIO') return <audio controls src={src} className="studio-result-media" />;
  if (job.capability === 'VIDEO')
    return <video controls src={src} className="studio-result-media" />;
  return <img src={src} alt="" className="studio-result-media" />;
}
